import { API_BASE } from "./config";
import React, { useEffect, useState, useRef } from "react";
import { AppBar, Toolbar, Typography, Button, Box, Grid, Card, CardContent, CardMedia, Stack, Alert, Snackbar, Container, IconButton, Divider, Link, } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from 'qrcode.react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


export default function Homepage({ token, role, logout }) {
  const [events, setEvents] = useState([]);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const eventiRef = useRef(null);



  useEffect(() => {
    fetchEvents();
    if (token) {
      fetchUserProfile();
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const res = await fetch(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        if (res.status === 401) {
          logout();
          return;
        }
        throw new Error("Errore nel recupero utente");
      }
      const data = await res.json();
      setUser(data);
    } catch (e) {
      console.error("Errore nel recupero utente", e);
      logout();
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_BASE}/events`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        const now = new Date();

        const upcomingEvents = data.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setDate(eventDate.getDate() + 1);
          return eventDate > now;
        });

        const sortedEvents = upcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

        // is_joined arriva già da /events per l'utente del token: non serve una chiamata
        // /events/{id}/is_joined per ogni evento (erano N richieste in più a ogni caricamento).
        setEvents(sortedEvents);
      }
    } catch (e) {
      console.error("Errore nel recupero eventi", e);
    }
  };

  const handleAction = (event) => {
    // Se l'evento è a pagamento, sempre link Instagram (loggato o no)
    if (event.is_paid) {
      window.open("https://ig.me/m/hedoneventi", "_blank");
      return;
    }

    // Se l'utente è già iscritto, non fare niente
    if (event.is_joined) {
      return;
    }

    // Se non è loggato, vai al login
    if (!token) {
      navigate("/login");
      return;
    }
    
    // Blocco utenti senza documento firmato per eventi gratuiti
    if (user && user.documento_firmato === false) {
      setFeedback({
        open: true,
        message: "Tesseramento incompleto: non puoi registrarti a questo evento gratuito.",
        severity: "warning"
      });
      return;
    }
     
    // Registrazione all'evento gratuito
    fetch(`${API_BASE}/events/${event.id}/participants/me`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) {
          // Il backend applica le stesse regole (evento gratuito, socio ammesso) e spiega
          // il motivo del rifiuto: mostriamo quello invece di un errore generico.
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Errore nell'iscrizione all'evento");
        }
        setFeedback({ open: true, message: "Iscritto all'evento con successo!", severity: "success" });
        fetchEvents();
      })
      .catch((err) => setFeedback({ open: true, message: err.message, severity: "warning" }));
  };

  return (
    <Box bgcolor="#670058" >
      <AppBar position="static" sx={{ bgcolor: '#fff' }} elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="logo.png"
              alt="Hedone Club"
              style={{ height: 40, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {token && (role === "admin" || role === "pr") && (
              <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                Vai alla Dashboard
              </Button>
            )}

            {token && role === "socio" && (
              <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                Area Socio
              </Button>
            )}

            {token ? (
              <Button className="font-poppins-regular" variant="outlined" onClick={logout}>Esci</Button>
            ) : (
              <>
                <Button className="font-poppins-regular" variant="outlined" onClick={() => navigate("/login")}>Accedi</Button>
                <Button className="font-poppins-regular" variant="contained" onClick={() => navigate("/register")}>Diventa socio</Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        textAlign: 'center', 
        py: 6,
        p:4,
        position: 'relative',
        backgroundImage: 'url("background.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(103, 0, 88, 0.2) 15%, rgba(103, 0, 88, 0.6) 50%, rgba(103, 0, 88, 0.95) 100%)',
          zIndex: 1,
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        }
      }}>

        <Box sx={{ textAlign: 'center', py: 4, }}>
          {token && user ? (
            <>
              <Typography color="secondary" paddingTop={6} className="font-poppins-regular" variant="body2" gutterBottom>
                Ciao {user.nome}!
              </Typography>
              <Typography className="font-poppins-bold" color="secondary" variant="h5" gutterBottom>
                Mostra il tuo QR Code all'ingresso per accedere agli eventi
              </Typography>

              {user && user.documento_firmato === false && (
                <Box severity="warning" sx={{ mb: 2, ml: 2, mr: 2, p: 2, borderRadius: 1, bgcolor: '#f6bfeeff' }}>
                  ⚠️ <b>Attenzione — Tesseramento incompleto</b><br />
                  <Typography className="font-poppins-regular" variant="body2">
                    Il tuo QR Code non è ancora attivo. Per accedere agli eventi e alle attività del club è necessario completare il processo di tesseramento.{" "}
                    <Link component="button" type="button" onClick={() => navigate("/dashboard")} sx={{ color: '#670058', fontWeight: 600, verticalAlign: 'baseline' }}>
                      Accedi all'area socio
                    </Link>.
                  </Typography>
                </Box>
              )}
              {/* Tessera scaduta: al socio "da rinnovare" non serve completare il tesseramento,
                  deve solo versare la quota per l'anno in corso (l'anno cambia al 1° gennaio) */}
              {user && user.stato_tessera === "scaduta" && (
                <Box severity="warning" sx={{ mb: 2, ml: 2, mr: 2, p: 2, borderRadius: 1, bgcolor: '#f6bfeeff' }}>
                  ⚠️ <b>Attenzione — Tessera da rinnovare</b><br />
                  <Typography className="font-poppins-regular" variant="body2">
                    La tua tessera per l'anno {new Date().getFullYear()} non è stata rinnovata.
                    Per rinnovare versa la quota annuale.
                  </Typography>
                </Box>
              )}
              <Typography className="font-poppins-regular" color="secondary" variant="body1" lineHeight={1.2} gutterBottom>
                Il tuo accesso esclusivo. <br /> Le tue vibes sono già pronte, non ti resta che goderti la notte.
              </Typography>
              <Box 
                sx={{ 
                  bgcolor: 'white', 
                  display: 'inline-block', 
                  p: 2, 
                  borderRadius: 2, 
                  boxShadow: 3,
                  mt: 2
                }}
              >
                <QRCodeCanvas value={`user:${user.id}`} size={150} />
              </Box>
              {user.numero_tessera && (
                <Typography className="font-poppins-regular" color="secondary" variant="body2" sx={{ mt: 1.5 }}>
                  Tessera N.: <b>{user.numero_tessera}</b>
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography className="font-poppins-bold" color="secondary" variant="h4" align="left" paddingTop={6} gutterBottom>Esplora nuove vibrazioni<br /> di piacere</Typography>
              <Typography className="font-poppins-regular" color="secondary" variant="h6" fontSize={16} align="left" lineHeight={1.3} gutterBottom>Il tuo accesso agli eventi esclusivi. <br /> Controlla gli eventi, gestisci la tua presenza e resta sempre nelle vibes.</Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  mt: 3
                }}
              >
                <Button 
                  className="font-poppins-regular" 
                  size="medium" 
                  color="secondary" 
                  variant="contained" 
                  sx={{ flex: '1 1 45%', maxWidth: '300px' }} 
                  onClick={() => navigate("/register")}
                >
                  Diventa socio
                </Button>
                <Button 
                  className="font-poppins-regular" 
                  size="medium" 
                  variant="contained"
                  onClick={() => {
                    eventiRef.current?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }}
                  sx={{ 
                    backgroundColor: "#e4ff5a",
                    color: "#670058",
                    flex: '1 1 45%', 
                    maxWidth: '300px',
                    '&:hover': {
                      backgroundColor: "#d4ef4a",
                    }
                  }}
                >
                  Scopri gli eventi
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Container sx={{ bgcolor: '#670058', p:4 }} maxWidth="md" ref={eventiRef}>
        <Typography className="font-poppins-bold" sx={{ color: '#e4ff5a' }} variant="h5" gutterBottom align="center">
          Prossimi eventi
        </Typography>
        
        {events.length === 0 ? (
          <Box sx={{ justifyContent: 'center', py: 4 }}>
            <Typography align="center" className="font-poppins-regular" color="secondary" variant="h6" gutterBottom>
              Al momento non ci sono eventi programmati.
            </Typography>
            <Typography align="center" className="font-poppins-regular" variant="body1" color="secondary" sx={{ mt: 2 }}>
              Torna a trovarci presto per scoprire i prossimi eventi!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {events.map(event => (
              <Grid item key={event.id} xs={12} sm={6} md={4}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    bgcolor: '#4d0041ff',
                    color: '#fff',
                    borderRadius: 3
                  }}
                >
                  {event.poster_filename && (
                    <CardMedia
                      component="img"
                      image={`${API_BASE}/static/posters/${event.poster_filename}`}
                      alt={event.title}
                      sx={{ 
                        width: '100%',
                        height: '100%',
                        borderRadius: '12px 12px 0 0'
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography 
                      className="font-poppins-bold" 
                      variant="h5" 
                      sx={{ 
                        color: '#e4ff5a',
                        textTransform: 'uppercase',
                        mb: 1
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    <Typography 
                      className="font-poppins-regular" 
                      variant="body2" 
                      sx={{ color: '#fff', mb: 2, opacity: 0.9 }}
                    >
                      {event.description}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography 
                        className="font-poppins-regular" 
                        variant="body1" 
                        sx={{ color: '#fff', mb: 0.5 }}
                      >
                        <strong>Dove:</strong> {event.luogo_evento}
                      </Typography>
                      
                      <Typography 
                        className="font-poppins-regular" 
                        variant="body1" 
                        sx={{ color: '#fff', mb: 0.5 }}
                      >
                        <strong>Quando:</strong> {new Date(event.date).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </CardContent>
                  
                  {/* LOGICA PULSANTE */}
                  {token && event.is_joined ? (
                    // Evento gratuito + loggato + già iscritto: solo indicatore, non cliccabile.
                    // Niente `disabled`: MUI lo renderebbe grigio e poco leggibile sul viola.
                    <Button
                      className="font-poppins-semibold"
                      variant="contained"
                      disableRipple
                      sx={{
                        m: 2, py: 1.5, borderRadius: 2,
                        bgcolor: '#2e7d32',
                        color: '#fff',
                        cursor: 'default',
                        '&:hover': { bgcolor: '#2e7d32' },
                      }}
                    >
                      ✓ Risulti già registrato
                    </Button>
                  ) :
                  
                  event.is_paid ? (
                    // Evento a pagamento: sempre "Contattaci"
                    <Button 
                      className="font-poppins-semibold"
                      variant="contained"
                      onClick={() => handleAction(event)} 
                      sx={{ 
                        m: 2, 
                        py: 1.5,
                        bgcolor: '#e4ff5a',
                        color: '#670058',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: '#d4ef4a'
                        }
                      }}
                    >
                      Contattaci
                    </Button>
                  ) : !token ? (
                    // Evento gratuito + non loggato
                    <Box sx={{ m: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 2 }}>
                      <Typography className="font-poppins-regular" variant="body2" sx={{ color: '#000' }}>
                        ⚠️ Effettua il login per registrarti all'evento
                      </Typography>
                    </Box>
                  ) : token && user && user.documento_firmato === false ? (
                    // Evento gratuito + loggato + tesseramento incompleto
                    <Box sx={{ m: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 2 }}>
                      <Typography className="font-poppins-regular" variant="body2" sx={{ color: '#000' }}>
                        ⚠️ Non puoi registrarti: Tesseramento incompleto.{" "}
                        <Link component="button" type="button" onClick={() => navigate("/dashboard")} sx={{ color: '#670058', fontWeight: 600, verticalAlign: 'baseline' }}>
                          Accedi all'area socio
                        </Link>.
                      </Typography>
                    </Box>
                  ) : (
                    // Evento gratuito + loggato + tesseramento completo + non iscritto: "Registrami"
                    <Button 
                      className="font-poppins-semibold"
                      variant="contained"
                      onClick={() => handleAction(event)} 
                      sx={{ 
                        m: 2, 
                        py: 1.5,
                        bgcolor: '#e4ff5a',
                        color: '#670058',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: '#d4ef4a'
                        }
                      }}
                    >
                      Registrami all'evento
                    </Button>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Container maxWidth="md" sx={{ py: 6, p:4 }}>
        <Typography className="font-poppins-bold" variant="h4" color="secondary" gutterBottom align="center" sx={{ mb: 4 }}>
          Domande frequenti
        </Typography>
        
        <Box sx={{ bgcolor: 'background.paper' }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="primary" className="font-poppins-semibold">
                1. Chi può accedere agli eventi Hedonè?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className="font-poppins-regular">
                L'accesso è riservato ai soci Hedonè APS. Solo i soci presenti nella lista digitale dell'evento possono entrare.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="primary" className="font-poppins-semibold">
                2. Come faccio a diventare socio?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className="font-poppins-regular">
                Se non sei ancora socio, registrati direttamente sulla web/app hedoneventi.it. Dopo la registrazione, contattaci subito per firmare il modulo di adesione, dove confermerai la volontà di diventare socio e verserai la quota annuale di €10. Hedonè si riserva il diritto di non accettare nuovi soci.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="primary" className="font-poppins-semibold">
                3. Come riservare l'ingresso a un evento?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className="font-poppins-regular">
                Per partecipare a un evento, devi essere inserito nella lista digitale dall'organizzazione Hedonè. Questo assicura che solo i soci confermati possano entrare.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="primary" className="font-poppins-semibold">
                4. Come funziona l'ingresso agli eventi?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className="font-poppins-regular">
                All'ingresso, mostra il tuo profilo sulla web/app con il tuo QR code personale. Il team Hedonè scannerizzerà il QR code per confermare l'accesso. Chi non è presente nella lista digitale non può entrare.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Divider sx={{ mb: 4, bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography className="font-poppins-semibold" variant="h5" gutterBottom sx={{ color: '#fff' }}>
            Seguici su
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <IconButton 
              aria-label="Instagram" 
              sx={{ color: '#fff' }}
              href="https://instagram.com/hedoneventi" 
              target="_blank"
            >
              <InstagramIcon sx={{ fontSize: 40 }} />
            </IconButton>
            
            <IconButton 
              aria-label="Facebook" 
              sx={{ color: '#fff' }}
              href="https://facebook.com/hedoneventi" 
              target="_blank"
            >
              <FacebookIcon sx={{ fontSize: 40 }} />
            </IconButton>
            
            <IconButton 
              aria-label="TikTok" 
              sx={{ color: '#fff' }}
              href="https://tiktok.com/@hedoneventi" 
              target="_blank"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </IconButton>
            
            <IconButton 
              aria-label="Telegram" 
              sx={{ color: '#fff' }}
              href="https://t.me/+UTiu3QNXUmc3YjM0" 
              target="_blank"
            >
              <TelegramIcon sx={{ fontSize: 40 }} />
            </IconButton>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
      >
        <Alert severity={feedback.severity}>{feedback.message}</Alert>
      </Snackbar>

      <Box component="footer" sx={{ bgcolor: '#eae7de', py: 3, mt: 5 }}>
        <Container maxWidth="md">
          <Typography className="font-poppins-regular" variant="body2" color="text.secondary" align="center">
            Associazione culturale Hedonè APS. Affiliazione Comitato Provinciale ASC di Pordenone APS — Sport e Cultura – Il mondo delle associazioni.
          </Typography>
          <Typography className="font-poppins-regular" variant="body2" color="text.secondary" align="center">
            C.F.: 91071660863 - Sede legale: Via Itria, 35, 94012, Barrafranca (EN)
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}